import { useMemo, useState } from "react";
import { Modal, Spin, message } from "antd";
import { Link } from "react-router-dom";
import { useGetProfileStatsQuery } from "../../../store/service/clientBetService";
import {
  useChangePasswordSelfMutation,
  useDeleteBankAccountMutation,
  useGetMyBankAccountsQuery,
  useSaveBankAccountMutation,
} from "../../../store/service/paymentService";
import ClientPaymentRequestTables from "./ClientPaymentRequestTables";
import "./Client.scss";

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const ClientProfile = () => {
  const { data, isLoading } = useGetProfileStatsQuery(undefined, {
    pollingInterval: 15000,
  });
  const { data: banksData, isLoading: banksLoading } = useGetMyBankAccountsQuery();
  const [changePassword, { isLoading: pwdLoading }] = useChangePasswordSelfMutation();
  const [saveBank, { isLoading: saveBankLoading }] = useSaveBankAccountMutation();
  const [deleteBank, { isLoading: deleteBankLoading }] = useDeleteBankAccountMutation();

  const [pwdOpen, setPwdOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [pwdTouched, setPwdTouched] = useState({});
  const [bankTouched, setBankTouched] = useState({});
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [accountName, setAccountName] = useState("");

  const stats = data?.data;
  const banks = banksData?.data || [];

  const initials = (stats?.username || "P")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const pwdErrors = useMemo(() => {
    const e = {};
    if (!oldPassword) e.oldPassword = "Current password is required";
    if (!newPassword) e.newPassword = "New password is required";
    else if (newPassword.length < 4) e.newPassword = "At least 4 characters required";
    else if (oldPassword && newPassword === oldPassword) {
      e.newPassword = "New password must be different from current";
    }
    if (!confirmPassword) e.confirmPassword = "Confirm your new password";
    else if (newPassword !== confirmPassword) e.confirmPassword = "Passwords do not match";
    return e;
  }, [oldPassword, newPassword, confirmPassword]);

  const bankErrors = useMemo(() => {
    const e = {};
    const acc = String(accountNumber || "").trim();
    if (!acc) e.accountNumber = "Account number is required";
    else if (!/^\d{9,18}$/.test(acc)) e.accountNumber = "Enter 9–18 digit account number";

    const code = String(ifsc || "").trim().toUpperCase();
    if (!code) e.ifsc = "IFSC code is required";
    else if (!IFSC_RE.test(code)) e.ifsc = "Enter a valid IFSC (e.g. SBIN0001234)";

    const name = String(accountName || "").trim();
    if (!name) e.accountName = "Account holder name is required";
    else if (name.length < 2) e.accountName = "Enter full account holder name";
    return e;
  }, [accountNumber, ifsc, accountName]);

  const resetPwdForm = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPwdTouched({});
  };

  const resetBankForm = () => {
    setBankName("");
    setAccountNumber("");
    setIfsc("");
    setAccountName("");
    setBankTouched({});
  };

  const onChangePassword = async () => {
    setPwdTouched({ oldPassword: true, newPassword: true, confirmPassword: true });
    if (Object.keys(pwdErrors).length) {
      message.warning(Object.values(pwdErrors)[0]);
      return;
    }
    try {
      const res = await changePassword({ oldPassword, newPassword }).unwrap();
      message.success(res.message || "Password changed");
      setPwdOpen(false);
      resetPwdForm();
    } catch (err) {
      message.error(err?.data?.message || "Could not change password");
    }
  };

  const onSaveBank = async () => {
    setBankTouched({ accountNumber: true, ifsc: true, accountName: true });
    if (Object.keys(bankErrors).length) {
      message.warning(Object.values(bankErrors)[0]);
      return;
    }
    try {
      const res = await saveBank({
        bank_name: bankName,
        account_number: accountNumber.trim(),
        ifsc: ifsc.trim().toUpperCase(),
        account_name: accountName.trim(),
        is_default: banks.length === 0,
      }).unwrap();
      message.success(res.message || "Bank account saved");
      setBankOpen(false);
      resetBankForm();
    } catch (err) {
      message.error(err?.data?.message || "Could not save bank account");
    }
  };

  const onDeleteBank = async (id) => {
    try {
      const res = await deleteBank({ id }).unwrap();
      message.success(res.message || "Bank account removed");
    } catch (err) {
      message.error(err?.data?.message || "Could not remove bank account");
    }
  };

  if (isLoading && !stats) {
    return (
      <div className="lotus-loading">
        <Spin />
      </div>
    );
  }

  const pwdShow = (f) => pwdTouched[f] && pwdErrors[f];
  const bankShow = (f) => bankTouched[f] && bankErrors[f];

  return (
    <div className="lotus-home client-profile-page">
      <div className="lotus-section-title">Profile</div>

      <div className="profile-hero">
        <div className="profile-avatar" aria-hidden="true">
          {initials}
        </div>
        <div className="profile-hero-name">{stats?.username || "Player"}</div>
        <div className="profile-hero-id">User ID · {stats?.userId}</div>
        <div className="profile-hero-balance">
          <span>Available balance</span>
          <strong>{Number(stats?.balance ?? 0).toFixed(2)}</strong>
        </div>
      </div>

      <div className="profile-action-row">
        <button type="button" className="profile-action-btn" onClick={() => setPwdOpen(true)}>
          Change password
        </button>
        <button
          type="button"
          className="profile-action-btn accent"
          onClick={() => setBankOpen(true)}
        >
          Add bank account
        </button>
      </div>

      <section className="profile-bank-section">
        <div className="profile-req-head">
          <h3>Saved bank accounts</h3>
          <span>{banks.length}</span>
        </div>
        {banksLoading ? (
          <div className="profile-inline-loading">
            <Spin size="small" />
          </div>
        ) : banks.length === 0 ? (
          <p className="profile-bank-empty">
            No bank account saved yet. Add one to use on withdrawals.
          </p>
        ) : (
          <ul className="profile-bank-list">
            {banks.map((bank) => (
              <li key={bank.id}>
                <div>
                  <strong>{bank.accountName}</strong>
                  <span className="mono">{bank.accountNumber}</span>
                  <span>
                    {bank.ifscCode}
                    {bank.bankName ? ` · ${bank.bankName}` : ""}
                    {bank.isDefault ? " · Default" : ""}
                  </span>
                </div>
                <button
                  type="button"
                  className="profile-bank-remove"
                  disabled={deleteBankLoading}
                  onClick={() => onDeleteBank(bank.id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="profile-quick-links compact">
        <Link to="/play/deposit" className="profile-link-btn dep">
          Deposit funds
        </Link>
        <Link to="/play/withdraw" className="profile-link-btn wd">
          Request withdrawal
        </Link>
        <Link to="/play/my-bets" className="profile-link-btn">
          Bet history
        </Link>
      </div>

      <ClientPaymentRequestTables filter="all" />

      <Modal
        title="Change password"
        open={pwdOpen}
        onCancel={() => {
          setPwdOpen(false);
          resetPwdForm();
        }}
        onOk={onChangePassword}
        okText="Update password"
        confirmLoading={pwdLoading}
        okButtonProps={{ disabled: Object.keys(pwdErrors).length > 0 }}
        destroyOnClose
      >
        <div className="profile-modal-form">
          <label className={pwdShow("oldPassword") ? "has-error" : ""}>
            Current password
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              onBlur={() => setPwdTouched((t) => ({ ...t, oldPassword: true }))}
              autoComplete="current-password"
            />
            {pwdShow("oldPassword") ? (
              <span className="field-error">{pwdErrors.oldPassword}</span>
            ) : null}
          </label>
          <label className={pwdShow("newPassword") ? "has-error" : ""}>
            New password
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onBlur={() => setPwdTouched((t) => ({ ...t, newPassword: true }))}
              autoComplete="new-password"
            />
            {pwdShow("newPassword") ? (
              <span className="field-error">{pwdErrors.newPassword}</span>
            ) : null}
          </label>
          <label className={pwdShow("confirmPassword") ? "has-error" : ""}>
            Confirm new password
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setPwdTouched((t) => ({ ...t, confirmPassword: true }))}
              autoComplete="new-password"
            />
            {pwdShow("confirmPassword") ? (
              <span className="field-error">{pwdErrors.confirmPassword}</span>
            ) : null}
          </label>
        </div>
      </Modal>

      <Modal
        title="Add bank account"
        open={bankOpen}
        onCancel={() => {
          setBankOpen(false);
          resetBankForm();
        }}
        onOk={onSaveBank}
        okText="Save account"
        confirmLoading={saveBankLoading}
        okButtonProps={{ disabled: Object.keys(bankErrors).length > 0 }}
        destroyOnClose
      >
        <div className="profile-modal-form">
          <label>
            Bank name
            <input
              type="text"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Optional"
            />
          </label>
          <label className={bankShow("accountNumber") ? "has-error" : ""}>
            Account number *
            <input
              type="text"
              inputMode="numeric"
              value={accountNumber}
              onChange={(e) =>
                setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 18))
              }
              onBlur={() => setBankTouched((t) => ({ ...t, accountNumber: true }))}
            />
            {bankShow("accountNumber") ? (
              <span className="field-error">{bankErrors.accountNumber}</span>
            ) : null}
          </label>
          <label className={bankShow("ifsc") ? "has-error" : ""}>
            IFSC code *
            <input
              type="text"
              value={ifsc}
              onChange={(e) =>
                setIfsc(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 11))
              }
              onBlur={() => setBankTouched((t) => ({ ...t, ifsc: true }))}
              maxLength={11}
            />
            {bankShow("ifsc") ? <span className="field-error">{bankErrors.ifsc}</span> : null}
          </label>
          <label className={bankShow("accountName") ? "has-error" : ""}>
            Account holder name *
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              onBlur={() => setBankTouched((t) => ({ ...t, accountName: true }))}
            />
            {bankShow("accountName") ? (
              <span className="field-error">{bankErrors.accountName}</span>
            ) : null}
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default ClientProfile;
